import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckIcon from '@mui/icons-material/Check'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import PrintIcon from '@mui/icons-material/Print'
import DescriptionIcon from '@mui/icons-material/Description'
import EventNoteIcon from '@mui/icons-material/EventNote'
import GroupsIcon from '@mui/icons-material/Groups'
import InsightsIcon from '@mui/icons-material/Insights'
import BoltIcon from '@mui/icons-material/Bolt'
import DashboardIcon from '@mui/icons-material/Dashboard'
import InventoryIcon from '@mui/icons-material/Inventory2'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PaymentsIcon from '@mui/icons-material/Payments'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import PeopleIcon from '@mui/icons-material/People'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import SettingsIcon from '@mui/icons-material/Settings'
import HistoryIcon from '@mui/icons-material/History'
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import StarRateIcon from '@mui/icons-material/StarRate'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { Assistant } from '@/components/Assistant'
import { REPORTS, reportPath } from '@/pages/reports/catalogue'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useNotifications, usePendingApprovals } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { PALETTE_OPTIONS } from '@/theme'
import { useAppearanceStore } from '@/themeStore'
import { CompanySwitcher } from './CompanySwitcher'
import { OutletSwitcher } from './OutletSwitcher'

const DRAWER_WIDTH = 252

/**
 * When this build was made, in the words a person would say it in.
 *
 * <p>Read once: it is stamped into the bundle and cannot change while the page
 * is open. An unreadable stamp is printed as it stands rather than as "Invalid
 * Date", because a wrong-looking version line is a thing somebody can report.</p>
 */
const buildDate = (() => {
  const built = new Date(__BUILT_AT__)
  return Number.isNaN(built.getTime())
    ? __BUILT_AT__
    : built.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
})()

/**
 * Whether the side menu is showing.
 *
 * <p>Remembered, because it is a preference rather than a mood: somebody
 * working down a wide table of figures hides it once and wants it to stay
 * hidden tomorrow.</p>
 */
const NAV_HIDDEN_KEY = 'catering.navHidden'

interface NavEntry {
  label: string
  to: string
  icon: JSX.Element
  /** Hidden unless the signed-in user holds this permission. */
  permission?: string
}

interface NavSection {
  heading: string
  entries: NavEntry[]
}

const NAV: NavSection[] = [
  {
    heading: 'Insight',
    entries: [
      { label: 'My desk', to: '/my-desk', icon: <DashboardIcon />, permission: 'order.read' },
      { label: 'Executive', to: '/dashboard/executive', icon: <InsightsIcon />, permission: 'report.executive' },
      { label: 'Sales', to: '/dashboard/sales', icon: <TrendingUpIcon />, permission: 'report.sales' },
      { label: 'Operations', to: '/dashboard/operations', icon: <EventNoteIcon />, permission: 'report.operations' },
      { label: 'Finance', to: '/dashboard/finance', icon: <PaymentsIcon />, permission: 'report.finance' },
    ],
  },
  {
    heading: 'Sales',
    entries: [
      { label: 'Enquiries', to: '/enquiries', icon: <AssignmentTurnedInIcon />, permission: 'enquiry.read' },
      { label: 'Customers', to: '/customers', icon: <PeopleIcon />, permission: 'customer.read' },
      { label: 'Hall availability', to: '/availability', icon: <CalendarMonthIcon />, permission: 'availability.read' },
      { label: 'Quotations', to: '/quotations', icon: <DescriptionIcon />, permission: 'quotation.read' },
    ],
  },
  {
    heading: 'Operations',
    entries: [
      { label: 'Orders', to: '/orders', icon: <EventNoteIcon />, permission: 'order.read' },
      { label: 'Kitchen', to: '/kitchen', icon: <RestaurantIcon />, permission: 'kitchen.read' },
      { label: 'Staff', to: '/staff', icon: <GroupsIcon />, permission: 'manpower.read' },
      { label: 'Logistics', to: '/logistics', icon: <LocalShippingIcon />, permission: 'logistics.read' },
      { label: 'Menu & packages', to: '/menu', icon: <MenuBookIcon />, permission: 'menu.read' },
      { label: 'Gate passes', to: '/gate-passes', icon: <InventoryIcon />, permission: 'equipment.read' },
      { label: 'EB readings', to: '/eb-readings', icon: <BoltIcon />, permission: 'utility.read' },
    ],
  },
  {
    heading: 'Finance & experience',
    entries: [
      { label: 'Invoices', to: '/invoices', icon: <ReceiptLongIcon />, permission: 'billing.read' },
      // Called what the customer is handed, not what the ledger does with it:
      // the counter says "give them a receipt", never "record a payment".
      { label: 'Receipts', to: '/receipts', icon: <PaymentsIcon />, permission: 'payment.read' },
      { label: 'Feedback', to: '/feedback', icon: <StarRateIcon />, permission: 'feedback.read' },
      { label: 'Notifications', to: '/notifications', icon: <NotificationsIcon /> },
      { label: 'Settings', to: '/settings', icon: <SettingsIcon />, permission: 'config.read' },
      {
        label: 'API configuration',
        to: '/api-configuration',
        icon: <CloudSyncIcon />,
        permission: 'config.read',
      },
      { label: 'Users and access', to: '/access', icon: <AdminPanelSettingsIcon />, permission: 'user.read' },
    ],
  },
  {
    heading: 'Reports',
    entries: [
      // The diary is not one of the catalogue's reports - it reads nothing and
      // shows nothing on screen, it is a book to print and write in - so it is
      // named here rather than generated from that list. Without an entry of
      // its own it sat on a landing page the menu never opens.
      {
        label: 'Booking diary',
        to: '/reports/booking-diary',
        // Its own icon: the ones for orders and for the menu are already
        // spoken for, and a menu that repeats an icon reads as one thing in
        // two places.
        icon: <AutoStoriesIcon />,
        permission: 'report.operations',
      },
      // Counts that open into names and times rather than a table to take
      // away, so it is named here for the same reason the diary is.
      {
        label: 'Documents printed',
        to: '/reports/documents-printed',
        icon: <PrintIcon />,
        permission: 'report.operations',
      },
      // Not built from the catalogue either: it reads the audit trail
      // directly rather than the report engine, and needs config.read -
      // the same permission the rest of the configuration screens use -
      // rather than a report permission.
      {
        label: 'Transaction log',
        to: '/audit-log',
        icon: <HistoryIcon />,
        permission: 'config.read',
      },
      // A screen of its own rather than one of the catalogue's reports: it
      // configures when and to whom, not a table to read on screen.
      {
        label: 'Scheduled reports',
        to: '/scheduled-reports',
        icon: <ScheduleSendIcon />,
        permission: 'report.schedule',
      },
      // The rest are built from the same list the report screen reads, so a
      // report added there appears here without anybody remembering to add it
      // twice.
      ...REPORTS.map((report) => ({
        label: report.name,
        to: reportPath(report.type),
        icon: <SummarizeIcon />,
        permission: report.permission,
      })),
    ],
  },
]

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(() => localStorage.getItem(NAV_HIDDEN_KEY) !== 'true')
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const { user, can, signOut } = useAuthStore()
  const { palette, mode, setPalette, setMode } = useAppearanceStore()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: approvals } = usePendingApprovals()
  const { data: notifications } = useNotifications()
  const unread = notifications?.content.filter((item) => !item.readAt).length ?? 0

  const visibleSections = NAV.map((section) => ({
    ...section,
    entries: section.entries.filter((entry) => !entry.permission || can(entry.permission)),
  })).filter((section) => section.entries.length > 0)

  // An account with no role sees an almost empty shell and no reason for it,
  // which reads as a broken application rather than as an account nobody has
  // finished setting up. Two different things are worth saying: holding no
  // permissions at all, and holding some but not the ones a screen needs.
  const holdsNothing = (user?.permissions?.length ?? 0) === 0

  const showNav = (open: boolean) => {
    setNavOpen(open)
    localStorage.setItem(NAV_HIDDEN_KEY, String(!open))
  }

  // What the side menu takes up when it is showing, and nothing when it is
  // not: the bar and the page beneath both close over the space it leaves.
  const railWidth = navOpen ? DRAWER_WIDTH : 0

  const activeLabel = visibleSections
    .flatMap((section) => section.entries)
    .find((entry) => location.pathname.startsWith(entry.to))?.label

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Catering Platform
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Banquet · Outdoor · Takeaway
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ overflowY: 'auto', flexGrow: 1, pb: 2 }}>
        {holdsNothing ? (
          <Alert severity="info" sx={{ m: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Nothing is set up for you yet
            </Typography>
            <Typography variant="caption">
              This account holds no role, so there is nothing it may open. An administrator
              can give it one under Users and access.
            </Typography>
          </Alert>
        ) : null}
        {visibleSections.map((section) => (
          <List
            key={section.heading}
            dense
            subheader={
              <ListSubheader disableSticky sx={{ bgcolor: 'transparent', fontSize: 11, letterSpacing: '0.08em' }}>
                {section.heading.toUpperCase()}
              </ListSubheader>
            }
          >
            {section.entries.map((entry) => (
              <ListItemButton
                key={entry.to}
                component={NavLink}
                to={entry.to}
                onClick={() => setMobileOpen(false)}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  '&.active': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                  '&.active .MuiListItemIcon-root': { color: 'primary.contrastText' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>{entry.icon}</ListItemIcon>
                <ListItemText primary={entry.label} primaryTypographyProps={{ fontSize: 14 }} />
              </ListItemButton>
            ))}
          </List>
        ))}
        {/* Entries can also be missing because the business does not run that
            part of the application, which is a different answer from "you may
            not". Said once, at the bottom, rather than against each gap. */}
        {!holdsNothing && visibleSections.length < NAV.length ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 3, pt: 1 }}
          >
            Some sections are hidden: either this business does not run them, or your role
            does not reach them.
          </Typography>
        ) : null}
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${railWidth}px)` },
          ml: { md: `${railWidth}px` },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen((open) => !open)}
            sx={{ mr: 2, display: { md: 'none' } }}
            aria-label="Open navigation"
          >
            <MenuIcon />
          </IconButton>
          {/* On a wide screen the menu is always there, which is most of the
              time what you want and occasionally 252 pixels you would rather
              give to the table you are reading. */}
          <Tooltip title={navOpen ? 'Hide the menu' : 'Show the menu'}>
            <IconButton
              edge="start"
              onClick={() => showNav(!navOpen)}
              sx={{ mr: 2, display: { xs: 'none', md: 'inline-flex' } }}
              aria-label={navOpen ? 'Hide the menu' : 'Show the menu'}
            >
              {navOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {activeLabel ?? 'Dashboard'}
          </Typography>

          <CompanySwitcher />
          <OutletSwitcher />

          <Tooltip title="Approvals waiting on you">
            <IconButton onClick={() => navigate('/settings?tab=approvals')} aria-label="Pending approvals">
              <Badge badgeContent={approvals?.length ?? 0} color="warning">
                <AssignmentTurnedInIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton onClick={() => navigate('/notifications')} aria-label="Notifications">
              <Badge badgeContent={unread} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Before the avatar, because it is a thing you use rather than a
              thing about you. */}
          <Assistant />

          <Tooltip title={user?.fullName ?? ''}>
            <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)} sx={{ ml: 1 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}>
                {user?.fullName?.slice(0, 1).toUpperCase() ?? '?'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 250 } } }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {user?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.roles.join(', ')}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />

            <ListSubheader disableSticky sx={{ bgcolor: 'transparent', fontSize: 11, letterSpacing: '0.08em' }}>
              APPEARANCE
            </ListSubheader>
            <Box sx={{ px: 2, pb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={mode}
                // A null value means the active button was clicked again; keep the current mode.
                onChange={(_, next) => next && setMode(next)}
                aria-label="Colour mode"
              >
                <ToggleButton value="light" aria-label="Light mode">
                  <LightModeIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Light
                </ToggleButton>
                <ToggleButton value="dark" aria-label="Dark mode">
                  <DarkModeIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Dark
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {PALETTE_OPTIONS.map((option) => (
              <MenuItem key={option.id} selected={palette === option.id} onClick={() => setPalette(option.id)}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: option.swatch,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
                {palette === option.id && <CheckIcon fontSize="small" color="primary" />}
              </MenuItem>
            ))}

            <Divider />
            <MenuItem
              onClick={async () => {
                setMenuAnchor(null)
                await signOut()
                navigate('/login')
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>

            {/* Which build this is, and when it last changed. Asked for over
                the phone - "what version are you on?" - and the answer was
                nowhere on the screen. Small, and out of the way of everything
                somebody opens this menu to actually do. */}
            <Divider />
            <Box sx={{ px: 2, pt: 0.5, pb: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Version {__APP_VERSION__}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11, display: 'block' }}
              >
                Updated {buildDate}
              </Typography>
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: railWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: navOpen ? 'block' : 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${railWidth}px)` },
          px: { xs: 2, md: 4 },
          py: 3,
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
